import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ErrorsService } from '@partners/errors';
import { takeOnce } from '@partners/utils';
import { map, Observable, tap } from 'rxjs';
import { IFormConfig, IFormFactory, IFormField, IFormResetOptions } from '../model';

@Injectable({
  providedIn: 'root'
})
export abstract class FeatureForm<T> {
  private _errorsService = inject(ErrorsService);

  protected formBuilder: FormBuilder;

  abstract createForm(model: unknown): Observable<IFormFactory>;

  constructor() {
    this.formBuilder = inject(FormBuilder);
  }

  /**
   * @description
   * build an ng reactive form using a custom config scheme as blueprint. the config scheme (TFormConfig)
   * contains the flattened representations of the final nested ng form. the key of the map is a string
   * annotation representing a nested form control's path, while the value of the map is the config node
   * of that control (or group or array);
   *
   * @param formConfig a config class instance, containing flattened map of fields & groups config nodes
   * @param formGroup an empty (new) or existing form group ref to which we add the nested controls
   * @returns a fully formed angular form
   */
  generate(formConfig: IFormConfig, formGroup: FormGroup<{}> = new FormGroup({})): FormGroup<{}> {
    try {
      /** get a string array of all the keys in the flattened config map */
      const { tokens } = formConfig;
      /** recursively build the nested ng reactive form */
      this.iterate(formGroup, tokens, formConfig);
    } catch (e) {
      this._errorsService.throwLocalError({ message: `Failed to generate form "${formConfig.name}" from config` });
    }
    return formGroup;
  }

  isFormValid(formFactory$: Observable<IFormFactory>): Observable<boolean> {
    return formFactory$.pipe(
      takeOnce(),
      tap(({ form }) => {
        form.markAllAsTouched();
      }),
      map(({ form }) => form.valid)
    );
  }

  resetForm(form: FormGroup, options?: IFormResetOptions<T>): void {
    form.reset();
    form.markAsPristine();
    form.markAsUntouched();
    form.updateValueAndValidity(); 
  }

  resetFactory(factory$: Observable<IFormFactory>): void {
    factory$.pipe(
      takeOnce(),
      tap(({ form, config }) => {
        this.resetForm(form);
      }))
      .subscribe();
  }
  
  /**
   * @description
   * recursively generate an ng reactive form
   * @param form the root|current form element (the parent of the next control)
   * @param tokens a string array of ALL the form controls' names (dot annotated forma ["firstName", "address.residence")
   * @param config a config map containing metadata for each form control
   */
  private iterate(form: AbstractControl, tokens: string[], config: IFormConfig) {
    while (tokens.length) {
      const token = tokens.shift();
      /** abstract ctrl might be: group | array | control */
      const abstractCtrl = form.get(token);

      if (abstractCtrl) {
        /** exit point for the prev recursion iteration */
        continue;
      }

      /** add a form control */
      const field = config.find(token);
      const descendants = config.filter(token);
      let nextCtrl = this.addFormCtrl(field, form, config);

      if (nextCtrl instanceof FormArray) {
        this.prepareArrayGroups(field.path, nextCtrl, config);
        // nextCtrl = (form as FormArray).at(field.index);
      }

      if (descendants?.length) {
        this.iterate(nextCtrl, descendants, config);
      }
    }
  }

  private prepareArrayGroups(formName: string, formArray: FormArray, formConfig: IFormConfig) {
    const { tokens } = formConfig;
    const flatEntries = tokens.filter((k) => k.startsWith(`${formName}`));
    /**
     * take all array's entry keys except the first entry key, since its the array's name
     * and we need its children. we transform this  {
     * "a",
     * "a.0.fieldA",
     * "a.0.fieldB",
     * "a.1.fieldA",
     * "a.1.fieldB"
     * }
     *
     * into this: {
     * "a.0.fieldA",
     * "a.0.fieldB",
     * "a.1.fieldA",
     * "a.1.fieldB"
     * }
     *
     * */
    const [, ...groupEntries] = flatEntries;

    /**
     * aggregate the array entries into unique index array. meaning we transform this: {
     * "a.0.fieldA",
     * "a.0.fieldB",
     * "a.1.fieldA",
     * "a.1.fieldB"
     * }
     *
     * into this: [0, 1]
     * */
    const arrayGroupsIndex = new Set([...groupEntries].map((entryKey) => entryKey.split('.')[1]));

    for (let i = 0; i < arrayGroupsIndex.size; i++) {
      /** create an empty form group for each array entry  */
      formArray.push(this.formBuilder.group({}));
    }
  }

  private addFormCtrl(field: IFormField<any>, form: AbstractControl<{}>, config?: IFormConfig): AbstractControl<{}> {
    const formRef = form as FormGroup;
    switch (field.elementType) {
      case 'group': {
        const group = this.formBuilder.group({});
        formRef.addControl(field.path, group);
        return group;
      }
      case 'array': {
        const array = this.formBuilder.array([]);
        formRef.addControl(field.path, array);
        return array;
      }
      default: {
        const isArray = form instanceof FormArray;
        /**
         * if the parent of the next control is an array - we cant add a control directly at it,
         * we need to get the control's group and add the control to it, we get the correct group
         * using the field's index value
         */
        if (isArray) {
          const arrayGroup = form.at(field.index) as FormGroup;
          arrayGroup.addControl(field.name, field.control);
        } else {
          formRef.addControl(field.name, field.control);
        }
        return field.control;
      }
    }
  }
}
